import React from 'react';
import { Box, Button, type ButtonProps, Typography } from '@mui/material';
import Save from '@mui/icons-material/Save';
import LoadingOverlayButton, {
    type LoadingOverlayButtonProps,
} from '../../../component/button/loading-overlay-button.component';
import i18n from '../../../i18n';
import { ReactComponent as IcBaselineDeleteForever } from '../../../static/images/svg/ic_baseline-delete-forever.svg';

/**
 * 打印详情卡片头部操作区容器：删除与保存横向排列，删除与保存间距 24px（theme.spacing(3)）。
 */
export function PrintDetailHeadActions(props: {
    /** 透传 BEM 类名（如 `print-device-page__detail-actions`） */
    className?: string;
    /** 子节点：一般为删除 + 保存 */
    children: React.ReactNode;
}): JSX.Element {
    const { className, children } = props;
    return (
        <Box
            className={className}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                flexShrink: 0,
            }}
        >
            {children}
        </Box>
    );
}

export type PrintDetailDeletePrinterButtonProps = Omit<ButtonProps, 'children'> & {
    /** 按钮文案（各页传入：删除打印机 / 删除打印组 / 删除打印头 / 删除打印尾）；不传则回退「删除打印机」 */
    label?: React.ReactNode;
};

/**
 * 详情头危险删除按钮；视觉对齐 Figma 2054:76229（填充辅助色-200 + SP1 文案/图标）；文案由 `label` 区分页面语义。
 */
export function PrintDetailDeletePrinterButton(
    props: PrintDetailDeletePrinterButtonProps,
): JSX.Element {
    const { label, sx, disabled, startIcon, ...rest } = props;
    return (
        <Button
            type="button"
            variant="text"
            disabled={disabled}
            disableElevation
            startIcon={
                startIcon ?? (
                    <Box
                        component="span"
                        aria-hidden
                        sx={{
                            display: 'inline-flex',
                            width: 32,
                            height: 32,
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'inherit',
                            '& svg': { width: 32, height: 32, display: 'block' },
                        }}
                    >
                        <IcBaselineDeleteForever />
                    </Box>
                )
            }
            sx={[
                {
                    textTransform: 'none',
                    borderRadius: '8px',
                    px: 2,
                    py: 1,
                    gap: 1,
                    minWidth: 0,
                    backgroundColor: 'var(--fill-neutral-200)',
                    color: 'var(--func-sp1)',
                    '&:hover': {
                        backgroundColor: 'var(--fill-neutral-200)',
                        opacity: 0.92,
                    },
                    '&.Mui-disabled': {
                        backgroundColor: 'var(--fill-neutral-200)',
                        color: 'var(--text-secondary)',
                        opacity: 0.5,
                    },
                },
                ...(Array.isArray(sx) ? sx : sx != null ? [sx] : []),
            ]}
            {...rest}
        >
            <Typography variant="subtitle1" component="span" color="inherit">
                {label ?? i18n.t('print_detail_delete_printer')}
            </Typography>
        </Button>
    );
}

export type PrintDetailSaveButtonProps = LoadingOverlayButtonProps;

/**
 * 详情头主保存按钮：统一 LoadingOverlayButton 默认值（类型、禁用 elevation、指示器尺寸）。
 */
export function PrintDetailSaveButton(props: PrintDetailSaveButtonProps): JSX.Element {
    const {
        startIcon,
        loadingIndicatorSize = 22,
        type = 'button',
        variant = 'contained',
        disableElevation = true,
        ...rest
    } = props;
    return (
        <LoadingOverlayButton
            {...rest}
            type={type}
            variant={variant}
            disableElevation={disableElevation}
            loadingIndicatorSize={loadingIndicatorSize}
            startIcon={startIcon ?? <Save />}
        />
    );
}
